/**
 * The paint shared by every field a user can type into or pick from.
 *
 * Border, fill and lift are one thing: a field with the border and fill but no
 * shadow reads as a read-only value box. Keeping them together is what makes
 * the shadow present by construction rather than by remembering to paste it.
 *
 * Paint only — padding, height, typography and focus handling differ per
 * surface and stay at the call site:
 *
 *   const INPUT_CLASSES = cn(FIELD_SURFACE, 'h-auto px-6 py-4 text-sm');
 *
 * Read-only value boxes (Ready to eat at, Consume by, the computed Amount
 * column) must not use this — they are deliberately flat.
 */

/** Border colour, fill and lift, no radius — for surfaces that set their own. */
export const FIELD_PAINT = [
  "border-dinnerplate-white bg-off-white shadow-field",
  // The lift means "you can type here", so it comes off when that stops being
  // true — otherwise a disabled Input stays lifted beside a disabled dropdown.
  "disabled:shadow-none aria-disabled:shadow-none read-only:shadow-none",
].join(" ");

export const FIELD_SURFACE = `rounded-input ${FIELD_PAINT}`;

/** The red border and fill of a field that fails validation. */
export const FIELD_INVALID_PAINT = "border-chilli-red-light bg-chilli-blush";

/** `FIELD_INVALID_PAINT` keyed on `aria-invalid`. Tailwind only sees variants spelled out in full. */
export const FIELD_ARIA_INVALID_PAINT = "aria-invalid:border-chilli-red-light aria-invalid:bg-chilli-blush";

/** For elements that draw their own border rather than inheriting one. */
export const FIELD_SURFACE_BORDERED = `rounded-input border ${FIELD_PAINT}`;

/**
 * The bordered surface for a painted wrapper `div` that hosts its own inner
 * input (an identifier box with inline actions). The editability guards are
 * omitted on purpose: CSS `:read-only` matches any non-editable element —
 * a plain div included — so `read-only:shadow-none` would strip the lift from
 * every wrapper. A wrapper whose inner field stops being editable opts out of
 * the lift itself.
 */
export const FIELD_WRAPPER_SURFACE =
  "rounded-input border border-dinnerplate-white bg-off-white shadow-field";

/**
 * Opt-out for toolbar filters. A filter selects what you are looking at rather
 * than holding a value you are editing, and sits in a row of flat buttons.
 * Filters reuse the field primitives, so they need this applied after them.
 */
export const FILTER_SURFACE = "shadow-none";
